/**
 * <AnnotationCanvas> (web) — le calque de dessin sur un canevas 2D.
 *
 * Le port de `common/liveMode/liveV3/AnnotationCanvas.web.js` : le canevas est créé à la main, hors du
 * rendu de React (React ne le réconcilie jamais, donc ne l'efface jamais), les points intermédiaires
 * qu'un navigateur regroupe sont relus (`getCoalescedEvents`, un trait sans saut), le curseur est un
 * crayon. Ce qui s'ajoute : la gomme (les traits touchés quittent le calque, l'annulation les rend),
 * l'encre résolue par le thème, et la taille lue sur la surface elle-même (`ResizeObserver`).
 *
 * Les props et leur sens sont ceux du calque natif (`AnnotationCanvas.tsx`).
 */
import React, { useEffect, useRef } from "react"
import { View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type AnnotationCanvasProps } from "./AnnotationCanvas"
import {
    addStroke,
    commitErase,
    eraseAt,
    startErase,
    type AnnotationPoint,
    type AnnotationStroke,
    type EraseGesture,
} from "./annotationStore"

export type { AnnotationCanvasProps } from "./AnnotationCanvas"

/** La portée de la gomme autour du pointeur. */
export const ERASER_RADIUS = 12

// Le crayon de l'ancien curseur (un dessin, pas une couleur de marque).
const PEN_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M3 21l1.65-3.8 2.15 2.15L3 21zm4.39-3.29l-2.1-2.1L16.95 3.95c.39-.39 1.02-.39 1.41 0l.71.71c.39.39.39 1.02 0 1.41L7.39 17.71z' fill='%234CAF50'/%3E%3C/svg%3E") 2 22, auto`
const ERASER_CURSOR = "cell"

function drawStroke(ctx: CanvasRenderingContext2D, stroke: AnnotationStroke) {
    const { points } = stroke
    if (points.length === 0) return
    ctx.beginPath()
    ctx.strokeStyle = stroke.color
    ctx.lineWidth = stroke.width
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.moveTo(points[0].x, points[0].y)
    // Un point seul se dessine comme un point (l'ancien `lineTo` sur lui-même).
    if (points.length === 1) ctx.lineTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y)
    ctx.stroke()
}

export function AnnotationCanvas(props: AnnotationCanvasProps) {
    const { store, enabled, ink = "role", erasing = false, strokeWidth = 4, version = 0, onChange, testID } = props
    const { theme } = useUnistyles()
    const color = theme.colors[ink]
    const containerRef = useRef<View>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const redrawRef = useRef<(() => void) | null>(null)

    // Les props lues par les gestionnaires, posés une fois : toujours fraîches.
    const live = useRef({ store, enabled, color, erasing, strokeWidth, onChange })
    live.current = { store, enabled, color, erasing, strokeWidth, onChange }

    useEffect(() => {
        const container = containerRef.current as unknown as HTMLElement | null
        if (container == null || typeof document === "undefined") return
        const canvas = document.createElement("canvas")
        canvas.style.position = "absolute"
        canvas.style.top = "0"
        canvas.style.left = "0"
        canvas.style.touchAction = "none"
        container.appendChild(canvas)
        canvasRef.current = canvas

        let current: AnnotationStroke | null = null
        let erase: EraseGesture = startErase()
        let pressing = false

        const redraw = () => {
            const ctx = canvas.getContext("2d")
            if (ctx == null) return
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            for (const stroke of live.current.store.strokes) drawStroke(ctx, stroke)
            if (current != null) drawStroke(ctx, current)
        }
        redrawRef.current = redraw

        const pointOf = (event: PointerEvent): AnnotationPoint => {
            const rect = canvas.getBoundingClientRect()
            return {
                x: (event.clientX - rect.left) * (canvas.width / (rect.width || 1)),
                y: (event.clientY - rect.top) * (canvas.height / (rect.height || 1)),
            }
        }

        const onDown = (event: PointerEvent) => {
            if (!live.current.enabled) return
            event.preventDefault()
            canvas.setPointerCapture?.(event.pointerId)
            pressing = true
            const point = pointOf(event)
            if (live.current.erasing) {
                erase = startErase()
                if (eraseAt(live.current.store, point, ERASER_RADIUS, erase)) redraw()
                return
            }
            current = { points: [point], color: live.current.color, width: live.current.strokeWidth }
            redraw()
        }
        const onMove = (event: PointerEvent) => {
            if (!pressing) return
            event.preventDefault()
            const coalesced = event.getCoalescedEvents?.()
            const events = coalesced != null && coalesced.length > 0 ? coalesced : [event]
            if (live.current.erasing) {
                let changed = false
                for (const each of events) {
                    changed = eraseAt(live.current.store, pointOf(each), ERASER_RADIUS, erase) || changed
                }
                if (changed) redraw()
                return
            }
            if (current == null) return
            const ctx = canvas.getContext("2d")
            for (const each of events) {
                current.points.push(pointOf(each))
                const { points } = current
                if (ctx != null && points.length >= 2) {
                    ctx.beginPath()
                    ctx.strokeStyle = current.color
                    ctx.lineWidth = current.width
                    ctx.lineCap = "round"
                    ctx.lineJoin = "round"
                    ctx.moveTo(points[points.length - 2].x, points[points.length - 2].y)
                    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y)
                    ctx.stroke()
                }
            }
        }
        const onUp = () => {
            if (!pressing) return
            pressing = false
            if (live.current.erasing) {
                if (commitErase(live.current.store, erase)) live.current.onChange?.()
                return
            }
            if (current == null) return
            addStroke(live.current.store, current)
            current = null
            live.current.onChange?.()
        }

        canvas.addEventListener("pointerdown", onDown)
        canvas.addEventListener("pointermove", onMove)
        canvas.addEventListener("pointerup", onUp)
        canvas.addEventListener("pointerleave", onUp)
        canvas.addEventListener("lostpointercapture", onUp)

        // La taille suit la surface (une page qui tourne, un centre qui s'élargit).
        const fit = () => {
            const width = Math.round(container.clientWidth)
            const height = Math.round(container.clientHeight)
            if (width === canvas.width && height === canvas.height) return
            canvas.width = width
            canvas.height = height
            canvas.style.width = `${width}px`
            canvas.style.height = `${height}px`
            redraw()
        }
        fit()
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null
        observer?.observe(container)

        return () => {
            observer?.disconnect()
            canvas.removeEventListener("pointerdown", onDown)
            canvas.removeEventListener("pointermove", onMove)
            canvas.removeEventListener("pointerup", onUp)
            canvas.removeEventListener("pointerleave", onUp)
            canvas.removeEventListener("lostpointercapture", onUp)
            container.removeChild(canvas)
            canvasRef.current = null
            redrawRef.current = null
        }
    }, [])

    // Le crayon pris ou posé, la gomme : le pointeur et le curseur.
    useEffect(() => {
        const canvas = canvasRef.current
        if (canvas == null) return
        canvas.style.pointerEvents = enabled ? "auto" : "none"
        canvas.style.cursor = enabled ? (erasing ? ERASER_CURSOR : PEN_CURSOR) : "default"
    }, [enabled, erasing])

    // Une autre activité (un autre magasin), ou des traits touchés par l'écran : on redessine.
    useEffect(() => {
        redrawRef.current?.()
    }, [store, version])

    return <View ref={containerRef} style={styles.layer} pointerEvents="box-none" testID={testID} />
}

const styles = StyleSheet.create(() => ({
    /** Toute la surface qui le porte, par-dessus son contenu ; ce qui se pose après lui (les outils) reste dessus. */
    layer: { position: "absolute" as const, top: 0, left: 0, right: 0, bottom: 0 },
}))
